import { createReducer, on } from '@ngrx/store';
import type {
  BiomapHabitatClassification,
  BiomapMsaComponent,
} from '../../../interfaces/biomap/IBiomapLayer';
import type { IBiomapProtectedAreaLookupResponse } from '../../../interfaces/biomap/IBiomapProtectedArea';
import type { IBiomapVectorKpiPayload } from '../../../interfaces/biomap/IBiomapExplorerKpi';
import * as BiomapActions from '../../actions/biomap/biomap.actions';

export const BIOMAP_FEATURE_KEY = 'biomap';

export interface BiomapExplorerKpiState {
  entities: Record<string, IBiomapVectorKpiPayload>;
  loading: Record<string, boolean>;
  errors: Record<string, string | null>;
}

export interface BiomapGeospatialState {
  selectedProtectedAreaUuid: string | null;
  selectedMsaHighValueAreaId: string | null;
}

export interface BiomapProtectedAreasState {
  response: IBiomapProtectedAreaLookupResponse | null;
  loading: boolean;
  error: string | null;
}

export interface BiomapState {
  habitatClassification: BiomapHabitatClassification;
  msaComponent: BiomapMsaComponent;
  explorerKpi: BiomapExplorerKpiState;
  protectedAreas: BiomapProtectedAreasState;
  geospatial: BiomapGeospatialState;
}

export const initialBiomapState: BiomapState = {
  habitatClassification: 'l1',
  msaComponent: 'overall',
  explorerKpi: {
    entities: {},
    loading: {},
    errors: {},
  },
  protectedAreas: {
    response: null,
    loading: false,
    error: null,
  },
  geospatial: {
    selectedProtectedAreaUuid: null,
    selectedMsaHighValueAreaId: null,
  },
};

export const biomapReducer = createReducer(
  initialBiomapState,
  on(BiomapActions.setBiomapHabitatClassification, (state, { classification }) => ({
    ...state,
    habitatClassification: classification,
  })),
  on(BiomapActions.setBiomapMsaComponent, (state, { component }) => ({
    ...state,
    msaComponent: component,
  })),
  on(
    BiomapActions.hydrateBiomapLayerSelection,
    (state, { habitatClassification, msaComponent }) => ({
      ...state,
      habitatClassification: habitatClassification ?? state.habitatClassification,
      msaComponent: msaComponent ?? state.msaComponent,
    }),
  ),
  on(BiomapActions.loadBiomapExplorerKpi, (state, { rasterLayerUuid }) => ({
    ...state,
    explorerKpi: {
      ...state.explorerKpi,
      loading: {
        [rasterLayerUuid]: true,
      },
      errors: {
        ...state.explorerKpi.errors,
        [rasterLayerUuid]: null,
      },
    },
  })),
  on(BiomapActions.loadBiomapExplorerKpiSuccess, (state, { rasterLayerUuid, payload }) => ({
    ...state,
    explorerKpi: {
      entities: {
        ...state.explorerKpi.entities,
        [rasterLayerUuid]: payload,
      },
      loading: {
        ...state.explorerKpi.loading,
        [rasterLayerUuid]: false,
      },
      errors: {
        ...state.explorerKpi.errors,
        [rasterLayerUuid]: null,
      },
    },
  })),
  on(BiomapActions.loadBiomapExplorerKpiFailure, (state, { rasterLayerUuid, error }) => ({
    ...state,
    explorerKpi: {
      ...state.explorerKpi,
      loading: {
        ...state.explorerKpi.loading,
        [rasterLayerUuid]: false,
      },
      errors: {
        ...state.explorerKpi.errors,
        [rasterLayerUuid]: error,
      },
    },
  })),
  on(BiomapActions.clearBiomapExplorerKpi, (state, { rasterLayerUuid }) => {
    const entities = { ...state.explorerKpi.entities };
    const loading = { ...state.explorerKpi.loading };
    const errors = { ...state.explorerKpi.errors };

    delete entities[rasterLayerUuid];
    delete loading[rasterLayerUuid];
    delete errors[rasterLayerUuid];

    return {
      ...state,
      explorerKpi: {
        entities,
        loading,
        errors,
      },
    };
  }),
  on(BiomapActions.setBiomapExplorerKpiYear, (state) => ({
    ...state,
    geospatial: {
      ...state.geospatial,
      selectedMsaHighValueAreaId: null,
    },
  })),
  on(BiomapActions.focusBiomapProtectedArea, (state, { protectedArea }) => ({
    ...state,
    geospatial: {
      ...state.geospatial,
      selectedProtectedAreaUuid: protectedArea.uuid,
    },
  })),
  on(BiomapActions.focusBiomapMsaHighValueArea, (state, { area }) => ({
    ...state,
    geospatial: {
      ...state.geospatial,
      selectedMsaHighValueAreaId: area.id,
    },
  })),
  on(BiomapActions.clearBiomapExplorerOverlays, (state) => ({
    ...state,
    geospatial: initialBiomapState.geospatial,
  })),
  on(
    BiomapActions.loadBiomapProjectProtectedAreas,
    BiomapActions.lookupBiomapProtectedAreas,
    (state) => ({
      ...state,
      protectedAreas: {
        response: null,
        loading: true,
        error: null,
      },
      geospatial: {
        ...state.geospatial,
        selectedProtectedAreaUuid: null,
      },
    }),
  ),
  on(BiomapActions.loadBiomapProtectedAreasSuccess, (state, { response }) => ({
    ...state,
    protectedAreas: {
      response,
      loading: false,
      error: null,
    },
  })),
  on(BiomapActions.loadBiomapProtectedAreasFailure, (state, { error }) => ({
    ...state,
    protectedAreas: {
      ...state.protectedAreas,
      loading: false,
      error,
    },
  })),
  on(BiomapActions.clearBiomapProtectedAreas, (state) => ({
    ...state,
    protectedAreas: initialBiomapState.protectedAreas,
    geospatial: {
      ...state.geospatial,
      selectedProtectedAreaUuid: null,
    },
  })),
  on(BiomapActions.resetBiomapState, () => initialBiomapState),
);
